import './ShippingLabelShow.scss';

import { EmailField, NumberField, RefreshButton, Show, TextField } from '@refinedev/antd';
import { useOne, useShow } from '@refinedev/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import PopupMenu from '@/components/general/PopupMenu';
import { ShowInfoGrid, ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { Country, phoneNumberMeta } from '@/utils/phoneNumbers';

export const ShippingLabelShow = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const hasMenuActions = useMemo(
    () => hasPermission('edit') || hasPermission('delete'),
    [hasPermission],
  );
  const {
    result: record,
    query: { isLoading },
  } = useShow();
  const {
    result: companyResult,
    query: { isFetching: companyIsLoading },
  } = useOne({
    resource: 'empresas',
    id: record?.company || '',
    queryOptions: {
      enabled: !!record && !!record.company,
    },
  });
  const countryCode = useMemo(() => Country[record?.localidad.provincia.pais?.id], [record]);

  return (
    <Show
      isLoading={isLoading || companyIsLoading}
      headerButtons={({ refreshButtonProps, editButtonProps, deleteButtonProps }) => (
        <>
          <RefreshButton {...refreshButtonProps} hideText />
          {hasMenuActions && (
            <PopupMenu
              menuContent={
                <>
                  <ProtectedEditButton {...editButtonProps} type="text" />
                  <ProtectedDeleteButton {...deleteButtonProps} type="text" />
                </>
              }
            />
          )}
        </>
      )}>
      <ShowInfoGrid className="ShippingLabelShow">
        <ShowInfoItem label="Id">
          <NumberField value={record?.id ?? '-'} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Descripción')}>
          <TextField value={record?.description} />
        </ShowInfoItem>
        <ShowInfoItem label={t('País')}>
          <TextField value={record?.localidad.provincia.pais.nombre} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Provincia')}>
          <TextField value={record?.localidad.provincia.nombre} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Localidad')}>
          <TextField value={record?.localidad.nombre} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Código postal')}>
          <TextField value={record?.postal_code} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Dirección')}>
          <TextField value={record?.address} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Email')}>
          <EmailField value={record?.email ?? '-'} />
        </ShowInfoItem>
        <ShowInfoItem label={t('Teléfono')}>
          <TextField
            value={
              record?.phone_number && countryCode
                ? `${phoneNumberMeta[countryCode].phoneCountry} ${record?.phone_number}`
                : '-'
            }
          />
        </ShowInfoItem>
        <ShowInfoItem label={t('Empresa')}>
          <LinkTextField
            admin
            to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record?.company}`}
            label={companyResult?.full_name ?? record?.company}
          />
        </ShowInfoItem>
        <ShowInfoItem label={t('Contacto')}>
          <TextField value={record?.contact ?? '-'} />
        </ShowInfoItem>
      </ShowInfoGrid>
    </Show>
  );
};
